import styles from "./styles.module.scss";
import { type Table as TableInstance, flexRender } from "@tanstack/react-table";
import { FetcherState } from "./common";
import cn from "classnames";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "~/components/ui/empty";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";

export interface CommonTabProps {
  selected: boolean;
}

const getPageItems = (current: number, total: number): (number | "ellipsis")[] => {
  const items: (number | "ellipsis")[] = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  items.push(1);
  if (left > 2) items.push("ellipsis");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < total - 1) items.push("ellipsis");
  if (total > 1) items.push(total);
  return items;
};

function TabTable<T>(props: {
  tableInstance: TableInstance<T>;
  fetcher?: FetcherState<any>;
  emptyTitle: string;
  emptyMessage: string;
  fluid?: boolean;
  paginationProps?: any;
  loading?: boolean;
}) {
  const table = props.tableInstance;
  const rows = table.getRowModel().rows;
  const pageIndex = table.getState().pagination.pageIndex;

  const tableHeader = (
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const sorted = header.column.getIsSorted();
            return (
              <TableHead
                key={header.id}
                title=""
                style={{ width: header.column.getSize() }}
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                className={cn(
                  "px-4 py-3.5 text-sm font-bold text-foreground border-x-0",
                  canSort && "cursor-pointer select-none"
                )}
              >
                <div className="flex items-center gap-1.5">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {canSort &&
                    (sorted ? (
                      sorted === "desc" ? (
                        <ChevronDown className="size-3.5" />
                      ) : (
                        <ChevronUp className="size-3.5" />
                      )
                    ) : (
                      <ChevronsUpDown className="size-3.5 opacity-40" />
                    ))}
                </div>
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );

  if (props.fetcher?.loading || props.loading) {
    const skeletonColumnCount = table.getHeaderGroups()[0]?.headers.length ?? 5;
    return (
      <div className={cn("flex flex-col bg-white", { [styles.tableFluid]: props.fluid ?? false })}>
        <Table className="whitespace-nowrap border-separate border-spacing-0">
          {tableHeader}
          <TableBody>
            {Array.from({ length: 6 }).map((_, rowIdx) => (
              <TableRow key={`skeleton-row-${rowIdx}`}>
                {Array.from({ length: skeletonColumnCount }).map((_, colIdx) => (
                  <TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`} className="px-4 py-4 border-x-0">
                    <Skeleton className="h-4 w-full max-w-[180px]" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Empty className={styles.noResultsMsg}>
        <EmptyHeader>
          <EmptyTitle>{props.emptyTitle}</EmptyTitle>
          <EmptyDescription>{props.emptyMessage}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  // Server tabs paginate via the cursor fetcher (fetcher.totalPages); client-only
  // tabs paginate via the table's pagination row model — max() picks the live one.
  const totalPages = Math.max(props.fetcher?.totalPages ?? 1, table.getPageCount() ?? 1);
  const currentPage = props.fetcher?.page ?? pageIndex + 1;
  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    const idx = p - 1;
    props.fetcher?.onPageChange(idx);
    table.setPageIndex(idx);
  };

  return (
    <div className={cn("flex flex-col bg-white", { [styles.tableFluid]: props.fluid ?? false })}>
      <Table className="whitespace-nowrap border-separate border-spacing-0">
        {tableHeader}
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              onClick={(row.original as any).onClick ? (row.original as any).onClick : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{ width: cell.column.getSize() }}
                  className="px-4 py-4 border-x-0"
                  data-column={cell.column.id}
                >
                  <div className="flex items-center">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <Pagination className="mt-4 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className={cn("cursor-pointer", currentPage <= 1 && "pointer-events-none opacity-50")}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(currentPage - 1);
                }}
              />
            </PaginationItem>
            {getPageItems(currentPage, totalPages).map((item, i) =>
              item === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    isActive={item === currentPage}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage(item);
                    }}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                className={cn("cursor-pointer", currentPage >= totalPages && "pointer-events-none opacity-50")}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(currentPage + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

export default TabTable;
